import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Menu, Search, X, Check, ArrowLeft } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Sidebar } from "./Sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export interface SearchFilter {
  id: string;
  label: string;
  icon?: React.ReactNode;
  isActive?: boolean;
}

interface PageWrapperProps {
  title: string;
  titleComponent?: React.ReactNode;
  titleDropdown?: React.ReactNode;
  isTitleDropdownOpen?: boolean;
  children: React.ReactNode;
  sidebarOpen: boolean;
  onSidebarOpenChange: (open: boolean) => void;
  actions?: React.ReactNode;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;
  searchFilters?: SearchFilter[];
  /** @deprecated Use activeSearchFilters instead */
  activeSearchFilter?: string | null;
  /** @deprecated Use onSearchFiltersChange instead */
  onSearchFilterChange?: (filterId: string | null) => void;
  /** Array of active filter IDs for multi-select */
  activeSearchFilters?: string[];
  /** Callback for multi-select filter changes */
  onSearchFiltersChange?: (filterIds: string[]) => void;
  /** Back button handler - when provided, shows back arrow on mobile (replaces hamburger) */
  onBack?: () => void;
  /** Sub-page title - shown instead of main title when onBack is provided */
  subTitle?: string;
  /** Icon for sub-page title */
  subTitleIcon?: React.ReactNode;
}

export const PageWrapper: React.FC<PageWrapperProps> = ({
  title,
  titleComponent,
  titleDropdown,
  isTitleDropdownOpen,
  children,
  sidebarOpen,
  onSidebarOpenChange,
  actions,
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  searchFilters,
  activeSearchFilter,
  onSearchFilterChange,
  activeSearchFilters,
  onSearchFiltersChange,
  onBack,
  subTitle,
  subTitleIcon,
}) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery || "");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Support both legacy single-select and new multi-select APIs
  const effectiveActiveFilters = activeSearchFilters ?? (activeSearchFilter ? [activeSearchFilter] : []);
  const hasActiveFilters = effectiveActiveFilters.length > 0;

  const handleSearchOpen = () => {
    // Initialize with current prop value when opening search
    setLocalSearchQuery(searchQuery || "");
    setIsSearchOpen(true);
  };

  // Focus input when search opens
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const handleSearchInput = (value: string) => {
    setLocalSearchQuery(value);
    onSearchChange?.(value);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
    setLocalSearchQuery("");
    onSearchChange?.("");
    // Don't clear filters when closing search - let user keep them active
  };

  const handleClearAll = () => {
    setIsSearchOpen(false);
    setLocalSearchQuery("");
    onSearchChange?.("");
    // Clear filters
    if (onSearchFiltersChange) {
      onSearchFiltersChange([]);
    } else {
      onSearchFilterChange?.(null);
    }
  };

  const handleFilterClick = (filterId: string) => {
    if (onSearchFiltersChange) {
      // Multi-select mode
      if (effectiveActiveFilters.includes(filterId)) {
        onSearchFiltersChange(effectiveActiveFilters.filter(id => id !== filterId));
      } else {
        onSearchFiltersChange([...effectiveActiveFilters, filterId]);
      }
    } else {
      // Legacy single-select mode
      if (activeSearchFilter === filterId) {
        onSearchFilterChange?.(null);
      } else {
        onSearchFilterChange?.(filterId);
      }
    }
  };

  const hasSearch = onSearchChange !== undefined;

  // Mobile: full-screen overlay with header
  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-background">
        {/* Mobile Header */}
        <div className="bg-white border-b border-gray-200 flex-shrink-0">
          <div className="px-4 py-3 flex items-center gap-3">
            {isSearchOpen ? (
              <>
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    ref={searchInputRef}
                    type="text"
                    placeholder={searchPlaceholder || t("search")}
                    value={localSearchQuery}
                    onChange={(e) => handleSearchInput(e.target.value)}
                    className="pr-4 h-10 pl-9"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleSearchClose}
                  className="flex-shrink-0"
                >
                  <X className="h-5 w-5" />
                </Button>
              </>
            ) : (
              <>
                {onBack ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onBack}
                    className="flex-shrink-0"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                ) : (
                  <Sheet open={sidebarOpen} onOpenChange={onSidebarOpenChange}>
                    <SheetTrigger asChild>
                      <Button variant="ghost" size="icon" className="flex-shrink-0">
                        <Menu className="h-5 w-5" />
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="p-0 w-54">
                      <Sidebar onNavigate={() => onSidebarOpenChange(false)} />
                    </SheetContent>
                  </Sheet>
                )}
                {onBack && subTitle ? (
                  <h1 className="font-semibold text-lg flex-1 flex items-center gap-2">
                    {subTitleIcon}
                    {subTitle}
                  </h1>
                ) : titleComponent ? (
                  <div className="flex-1">{titleComponent}</div>
                ) : (
                  <h1 className="font-semibold text-lg flex-1">{title}</h1>
                )}
                {hasSearch && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleSearchOpen}
                    className="flex-shrink-0 relative"
                  >
                    <Search className="h-5 w-5" />
                    {/* Indicator dot when filters are active */}
                    {hasActiveFilters && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
                    )}
                  </Button>
                )}
                {actions}
              </>
            )}
          </div>

          {/* Active filter chips - shown when search is closed but filters are active */}
          {!isSearchOpen && hasActiveFilters && (
            <div className="px-4 pb-3">
              <div className="flex flex-wrap gap-2">
                {effectiveActiveFilters.map((filterId) => {
                  const filter = searchFilters?.find(f => f.id === filterId);
                  if (!filter) return null;
                  return (
                    <button
                      key={filterId}
                      onClick={() => handleFilterClick(filterId)}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 rounded text-xs font-medium text-primary hover:bg-primary/20"
                    >
                      {filter.icon}
                      {filter.label}
                      <X className="h-3 w-3" />
                    </button>
                  );
                })}
                {effectiveActiveFilters.length > 1 && (
                  <button
                    onClick={handleClearAll}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-700"
                  >
                    {t('clearAll')}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Title Dropdown - shown when title is tapped (e.g., calendar picker) */}
          {!isSearchOpen && isTitleDropdownOpen && titleDropdown && (
            <div className="px-4 pb-3">
              {titleDropdown}
            </div>
          )}

          {/* Search Filters - shown when search is open */}
          {isSearchOpen && searchFilters && searchFilters.length > 0 && (
            <div className="px-4 pb-3 max-h-32 overflow-y-auto">
              <div className="flex flex-wrap gap-2">
                {searchFilters.map((filter) => {
                  const isActive = effectiveActiveFilters.includes(filter.id);
                  return (
                    <button
                      key={filter.id}
                      onClick={() => handleFilterClick(filter.id)}
                      className={cn(
                        "inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors",
                        isActive
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200"
                      )}
                    >
                      {isActive ? <Check className="h-4 w-4" /> : filter.icon}
                      <span>{filter.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    );
  }

  // Desktop: inline header bar
  return (
    <div className="flex flex-col h-full">
      {/* Desktop Header */}
      <div className="bg-background border-b border-border flex-shrink-0">
        <div className="px-6 flex items-center gap-4 h-[60px]">
          {titleComponent ? (
            <div className="flex-shrink-0">{titleComponent}</div>
          ) : (
            <h1 className="text-lg font-medium text-foreground-strong flex-shrink-0">{title}</h1>
          )}
          {isSearchOpen ? (
            <>
              <div className="relative max-w-2xl w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  ref={searchInputRef}
                  type="text"
                  placeholder={searchPlaceholder || t("search")}
                  value={localSearchQuery}
                  onChange={(e) => handleSearchInput(e.target.value)}
                  className="pr-4 h-9 pl-9"
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSearchClose}
                className="flex-shrink-0"
              >
                <X className="h-5 w-5" />
              </Button>
              <div className="flex-1" />
            </>
          ) : (
            <>
              {/* Active filter chips when search is closed */}
              {hasActiveFilters && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  {effectiveActiveFilters.map((filterId) => {
                    const filter = searchFilters?.find(f => f.id === filterId);
                    if (!filter) return null;
                    return (
                      <button
                        key={filterId}
                        onClick={() => handleFilterClick(filterId)}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 rounded text-xs font-medium text-primary hover:bg-primary/20"
                      >
                        {filter.icon}
                        {filter.label}
                        <X className="h-3 w-3" />
                      </button>
                    );
                  })}
                  {effectiveActiveFilters.length > 1 && (
                    <button
                      onClick={handleClearAll}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-700"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              )}
              {hasSearch && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleSearchOpen}
                  className="flex-shrink-0 relative"
                >
                  <Search className="h-5 w-5" />
                  {/* Indicator dot when filters are active */}
                  {hasActiveFilters && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
                  )}
                </Button>
              )}
              <div className="flex-1" />
            </>
          )}
          {actions}
        </div>

        {/* Search Filters - shown when search is open */}
        {isSearchOpen && searchFilters && searchFilters.length > 0 && (
          <div className="px-6 pb-3 max-h-32 overflow-y-auto">
            <div className="flex flex-wrap gap-2">
              {searchFilters.map((filter) => {
                const isActive = effectiveActiveFilters.includes(filter.id);
                return (
                  <button
                    key={filter.id}
                    onClick={() => handleFilterClick(filter.id)}
                    className={cn(
                      "inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors",
                      isActive
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200"
                    )}
                  >
                    {isActive ? <Check className="h-4 w-4" /> : filter.icon}
                    <span>{filter.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-white">
        {children}
      </div>
    </div>
  );
};

// Keep the old name as an alias for backwards compatibility
export const MobilePageWrapper = PageWrapper;
